import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Key } from "@phosphor-icons/react";
import { Logo } from "@/components/common/Logo";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <Logo variant="horizontal" height={40} />
          <span className="font-head text-2xl font-semibold sr-only">Vozelto</span>
        </div>
        <div className="card-matte p-8" data-testid="reset-card">
          <div className="flex items-center gap-2 mb-1"><Key size={20} className="brand-text" weight="fill" />
            <h1 className="font-head text-xl font-semibold">Set a new password</h1></div>
          {!token ? (
            <div className="text-center mt-4">
              <p className="text-red-400 text-sm" data-testid="reset-error">Missing or invalid reset link.</p>
              <Button onClick={() => navigate("/forgot-password")} className="mt-4 bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">Request a new link</Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#9a9a9a] mb-6 mt-1">Choose a new password for your account.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className="label-caps">New password</Label>
                  <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 8 characters" className="bg-[#10101A] border-white/10 mt-1" />
                </div>
                <div>
                  <Label className="label-caps">Confirm password</Label>
                  <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter password" className="bg-[#10101A] border-white/10 mt-1" />
                </div>
                <Button data-testid="reset-submit" type="submit" disabled={busy} className="w-full bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">{busy ? "Updating…" : "Update password"}</Button>
              </form>
            </>
          )}
          <div className="mt-5">
            <Link data-testid="reset-back-login" to="/login" className="text-xs text-[#9a9a9a] hover:text-white">Back to sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
